
import { useDebounce } from '@uidotdev/usehooks'
import { useNavigate } from '@tanstack/react-router'
import { useTranslations } from '@/hooks/useTranslations'
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { Loader } from '@/components/common/Loader'
// import { useConfirmDeleteUserModal, useConfirmInviteModal, useInvitedWarningModal } from '@/components/common/Modals'
import { toast } from '@/components/common/Toast'
import { Button, LinkButton } from '@/components/ui'
import { UserDropdown, UserOrganizationStatus, UserStatus } from '@/constants/user'
// import {
//   useDeleteUserOrganization,
//   useGetOrganizationUserList,
//   useResendInviteUserOrganization,
// } from '@/modules/user/services/user'

import { useDatatableHeaders } from './columns'
// import { useConfirmDownloadModal } from '@/components/common/Modals/ConfirmDownloadModal'
import { DateFormat, formatDate } from '@/utils/date'
import { useDataTable } from '@/hooks/useDataTable'
import { DEFAULT_PAGE_SIZE_OPTIONS } from '@/constants/datatable'
import { DataTable } from '@/components/common/DataTable/DataTable'
import { Profile, useChangePassword, useCreateUser, useDeleteUser, useGetUserList, useUpdateUser } from '@/modules/user/services/user'
import ModalChangePassword from './ModalChangePassword'


export const UserManagementList = () => {
  const t = useTranslations('admin.user_management')
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<string>(UserOrganizationStatus.ALL)
  const debouncedSearchTerms = useDebounce(search, 500)
  const [searchTerms, setSearchTerms] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [userSelected, setUserSelected] = useState<string | null>(null)
  const [isOpenChangePassword, setIsOpenChangePassword] = useState(false)

  // const { ConfirmInviteModal, showConfirmInviteModal } = useConfirmInviteModal()
  // const { InvitedWarningModal, showInvitedWarningModal } = useInvitedWarningModal()
  // const { ConfirmDeleteUserModal, showConfirmDeleteUserModal } = useConfirmDeleteUserModal()
  // const { ConfirmDownloadModal, showConfirmDownloadModal } = useConfirmDownloadModal()


  const $apiDeleteUser = useDeleteUser()
  const $apiUpdateUser = useUpdateUser()
  const $apiChangePassword = useChangePassword()
  const onClickUserDropdownEvent = useCallback(
    async (eventName: string, item: Profile) => {
      switch (eventName) {
        // case UserDropdown.VIEW:
        //   navigate({ to: `/` })
        //   break
        case UserDropdown.EDIT:
          navigate({ to: `/admin/user/${item.id}/edit` })
          break
        case UserDropdown.DELETE:
          await deleteUser(item)
          break
        case UserDropdown.STATUS:
          await updateUser(item)
          break
        case UserDropdown.CHANGE_PASSWORD:
          setUserSelected(item.id)
          setIsOpenChangePassword(true)
          break
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const deleteUser = async (item: Profile) => {
    try {
      await $apiDeleteUser.mutateAsync({ id: item.id });
      toast.toastSuccess('ลบข้อมูลสำเร็จ')
    } catch (error) {
      toast.toastFailed('ลบข้อมูลไม่สำเร็จ')
    }
  }


  const updateUser = async (item: Profile) => {
    try {
      await $apiUpdateUser.mutateAsync({
        id: item.id,
        username: item.username,
        firstname: item.firstname,
        lastname: item.lastname,
        roleId: item.role_id,
        status: item.status === UserStatus.ACTIVE ? UserStatus.INACTIVE : UserStatus.ACTIVE
      });
      toast.toastSuccess('บันทึกข้อมูลสำเร็จ')
    } catch (error) {
      toast.toastFailed('บันทึกข้อมูลไม่สำเร็จ')
    }
  }

  const { columns } = useDatatableHeaders({ search: searchTerms, onClickUserDropdownEvent })

  const { dataTableProps, state, setData, setTotalPages, setPagination } = useDataTable({
    columns,
    showPageSize: true,
    showPagination: true,
    isLoading: false,
    pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
    sorting: [
      {
        id: 'created_at',
        desc: true,
      },
    ],
  })

  const { data, isPending } = useGetUserList({
    page: state.pagination.page,
    pageSize: state.pagination.pageSize,
    search: searchTerms,
    status,
    sort: state.sorting,
  })

  useEffect(() => {
    setSearchTerms(debouncedSearchTerms)
  }, [debouncedSearchTerms])

  useEffect(() => {

    setData(data?.data ?? [])
    setTotalPages(data?.meta?.totalPages ?? 1)
  }, [data, setData, setTotalPages])

  useEffect(() => {
    setPagination({ pageIndex: 0, pageSize: state.pagination.pageSize })
  }, [search, status])

  // const handleClickResendInvitation = async (user: any | null) => {
  //   try {
  //     setIsLoading(true)
  //     if (!user) {
  //       throw new Error('User is not found')
  //     }

  //     await $apiResendInvite.submitResend({
  //       orgId,
  //       userId: user.id,
  //     })

  //     toast.toastSuccess(<HighlightWord message={tForm('toast.resend_invitation_success', { email: user.email })} />)
  //   } catch (error: any) {
  //     console.error(error)
  //     toast.toastFailed(tForm('toast.resend_invitation_failed'))
  //   } finally {
  //     setIsLoading(false)
  //     setUserSelected(null)
  //   }
  // }

  // const handleClickDeleteUser = async (user: any | null) => {
  //   try {
  //     setIsLoading(true)
  //     if (!user) {
  //       throw new Error('User is not found')
  //     }

  //     await $apiDeleteUser.submitDelete({
  //       orgId,
  //       userId: user.id,
  //     })

  //     toast.toastSuccess(<HighlightWord message={tForm('toast.delete_success', { email: user.email })} />)
  //     refetch()
  //   } catch (error) {
  //     console.error(error)
  //     toast.toastFailed(tForm('toast.delete_failed'))
  //   } finally {
  //     setIsLoading(false)
  //     setUserSelected(null)
  //   }
  // }

  const isHasFilter = !!search || status !== UserOrganizationStatus.ALL

  return (
    <Fragment>
      <div className="relative">
        <div className="relative mt-4 p-4 md:p-8">
          <DataTable
            {...dataTableProps}
            isFilter={isHasFilter}
            isLoading={isPending}
          />
        </div>
        {isLoading && <Loader.FullScreen />}
      </div>
      <ModalChangePassword
        handleChangePassword={async (password) => {
          try {
            await $apiChangePassword.mutateAsync({ id: userSelected ?? '', newPassword: password })
            toast.toastSuccess('เปลี่ยนรหัสผ่านสำเร็จ')
          } catch (error) {
            toast.toastFailed('เปลี่ยนรหัสผ่านไม่สำเร็จ')
          } finally {
            setIsOpenChangePassword(false)
          }
        }}
        isOpen={isOpenChangePassword}
        onClose={() => setIsOpenChangePassword(false)}
      />
    </Fragment>
  )
}
